import { checkInput } from '@/utils/InputChecker';
import { normalizeEnglishTextForTts } from '@/utils/TextNormalization';

const SNIPPET_RADIUS = 18;
const MOST_COMMON_LIMIT = 5;

function uniqueCharacters(chars) {
    return [...new Set(chars)];
}

function getLineText(line) {
    if (line?.text === 'NULL') return '';
    return String(line?.text || '');
}

function isEmptyTtsLine(text) {
    const normalized = String(text || '').trim();
    return normalized === '' || normalized === 'NULL';
}

function isAllowedTtsCharacter(ch) {
    const normalizedText = normalizeEnglishTextForTts(ch);
    if (!normalizedText) return false;
    return Array.from(normalizedText).every(normalizedCh =>
        checkInput(normalizedCh, Number.MAX_SAFE_INTEGER).hasOnlyAllowed
    );
}

function formatUnsupportedCharacter(ch) {
    const code = ch.codePointAt(0);
    const codePoint = `U+${code.toString(16).toUpperCase().padStart(4, '0')}`;
    if (
        code <= 0x001F
        || (code >= 0x007F && code <= 0x009F)
        || (code >= 0x200B && code <= 0x200D)
        || code === 0xFEFF
    ) {
        return codePoint;
    }
    return `${ch} (${codePoint})`;
}

function escapeSnippetWhitespace(text) {
    return text
        .replace(/\r/g, '\\r')
        .replace(/\n/g, '\\n')
        .replace(/\t/g, '\\t');
}

function buildSnippet(chars, charIndex) {
    const start = Math.max(0, charIndex - SNIPPET_RADIUS);
    const end = Math.min(chars.length, charIndex + SNIPPET_RADIUS + 1);
    const prefix = start > 0 ? '...' : '';
    const suffix = end < chars.length ? '...' : '';
    return escapeSnippetWhitespace(`${prefix}${chars.slice(start, end).join('')}${suffix}`);
}

function pluralize(count, singular, plural = `${singular}s`) {
    return `${count} ${count === 1 ? singular : plural}`;
}

export function removeUnsupportedTtsCharacters(text) {
    return Array.from(normalizeSupportedTtsText(text)).filter(isAllowedTtsCharacter).join('');
}

export function normalizeSupportedTtsText(text) {
    return normalizeEnglishTextForTts(text);
}

export function findUnsupportedCharacterIssues(lines) {
    return (Array.isArray(lines) ? lines : []).reduce((issues, line, lineIndex) => {
        const text = getLineText(line);
        if (isEmptyTtsLine(text)) return issues;

        const chars = Array.from(text);
        chars.forEach((ch, charIndex) => {
            if (!isAllowedTtsCharacter(ch)) {
                issues.push({
                    lineIndex,
                    column: charIndex + 1,
                    character: ch,
                    characterLabel: formatUnsupportedCharacter(ch),
                    snippet: buildSnippet(chars, charIndex),
                });
            }
        });
        return issues;
    }, []);
}

export function formatUnsupportedLineList(issues) {
    const lineNumbers = uniqueCharacters(issues.map(issue => issue.lineIndex + 1));
    if (lineNumbers.length <= 3) {
        return `${lineNumbers.length === 1 ? 'Line' : 'Lines'} ${lineNumbers.join(', ')}`;
    }
    return `Lines ${lineNumbers.slice(0, 3).join(', ')} and ${lineNumbers.length - 3} more`;
}

export function summarizeUnsupportedCharacterIssues(issues) {
    const lineCount = uniqueCharacters(issues.map(issue => issue.lineIndex)).length;
    const mostCommon = summarizeMostCommonUnsupportedCharacters(issues);
    const summary = `Found ${pluralize(issues.length, 'unsupported character')} across ${pluralize(lineCount, 'line')}.`;
    return mostCommon ? `${summary} Most common: ${mostCommon}.` : summary;
}

export function summarizeMostCommonUnsupportedCharacters(issues) {
    const counts = issues.reduce((map, issue) => {
        map.set(issue.characterLabel, (map.get(issue.characterLabel) || 0) + 1);
        return map;
    }, new Map());
    return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, MOST_COMMON_LIMIT)
        .map(([characterLabel, count]) => count === 1 ? characterLabel : `${characterLabel} x${count}`)
        .join(', ');
}

export function formatUnsupportedCharacterIssueDetail(issue) {
    return `Line ${issue.lineIndex + 1}, column ${issue.column}: ${issue.characterLabel} in "${issue.snippet}"`;
}
